import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { apiClient } from "src/api/client";
import closeIcon from "src/assets/icons/closeGray.svg";
import { ErrorBlock } from "src/components/atoms/ErrorBlock";
import { useAppDispatch } from "src/store/hooks";
import { closeModal, selectModalState } from "src/store/slices/modalSlice";
import { RootState } from "src/store/store";
import { getErrorMessage } from "src/utils/errors/getErrorMessage";

import styles from "../AccountingSetup/styles.module.css";

type HttpError = { response?: { status?: number } };
const today = () => new Date().toISOString().slice(0, 10);

export const CompanyEquityModal = () => {
  const dispatch = useAppDispatch();
  const modal = useSelector((state: RootState) => selectModalState(state));
  const data = modal.data.companyEquityModal as
    | { companyId: number; companyName: string }
    | undefined;
  const [date, setDate] = useState(today());
  const [paidInCapital, setPaidInCapital] = useState("0");
  const [retainedEarnings, setRetainedEarnings] = useState("0");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const close = () => dispatch(closeModal({ type: "companyEquityModal" }));

  useEffect(() => {
    if (!data?.companyId) return;
    setLoading(true);
    setError(null);
    apiClient.getCompanyOpeningEquity(data.companyId)
      .then((result) => {
        setDate(result.as_of_date);
        setPaidInCapital(result.paid_in_capital);
        setRetainedEarnings(result.retained_earnings);
      })
      .catch((requestError: HttpError) => {
        if (requestError.response?.status !== 404) setError(getErrorMessage(requestError));
      })
      .finally(() => setLoading(false));
  }, [data?.companyId]);

  if (!data) return null;
  const openingTotal = Number(paidInCapital || 0) + Number(retainedEarnings || 0);

  const save = async () => {
    const paid = Number(paidInCapital);
    const retained = Number(retainedEarnings);
    if (!date) return setError("Accounting start date is required");
    if (!Number.isFinite(paid) || !Number.isFinite(retained)) return setError("Enter valid amounts");
    try {
      setSaving(true);
      setError(null);
      await apiClient.saveCompanyOpeningEquity(data.companyId, {
        as_of_date: date,
        paid_in_capital: paid,
        retained_earnings: retained,
      });
      close();
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.overlay} onMouseDown={close}>
      <div className={styles.modal} role="dialog" aria-modal="true" onMouseDown={(event) => event.stopPropagation()}>
        <div className={styles.header}>
          <div><h2>Opening equity</h2><p>{data.companyName}. Values at the start of accounting in PinFin.</p></div>
          <button className={styles.closeButton} type="button" onClick={close} aria-label="Close"><img src={closeIcon} alt="" /></button>
        </div>
        <div className={styles.content}>
          {loading ? <div className={styles.empty}>Loading equity...</div> : <>
            <div className={styles.formGrid}>
              <label className={styles.field}><span>Accounting start date</span><input type="date" value={date} onChange={(event) => setDate(event.target.value)} /></label>
              <label className={styles.field}><span>Paid-in capital</span><input type="number" step="0.01" value={paidInCapital} onChange={(event) => setPaidInCapital(event.target.value)} /></label>
              <label className={styles.field}><span>Retained earnings</span><input type="number" step="0.01" value={retainedEarnings} onChange={(event) => setRetainedEarnings(event.target.value)} /></label>
            </div>
            <div className={styles.summary}>Opening Equity: <strong>${openingTotal.toLocaleString("en-US", { minimumFractionDigits: 2 })}</strong></div>
            <ErrorBlock error={error} />
            <div className={styles.actions}><button className={styles.secondary} type="button" onClick={close}>Cancel</button><button className={styles.button} type="button" onClick={save} disabled={saving}>{saving ? "Saving..." : "Save equity"}</button></div>
          </>}
        </div>
      </div>
    </div>
  );
};
